// Tremor Input [v2.0.0]

import type { RefCallback, ReactElement } from 'react'
import { useRef, useState } from 'react'
import { tv, cn } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import { Slot } from './Slot'
import { SearchIcon } from 'lucide-react'
import { useMergeRefs } from 'react-merge-refs'
import type { HTMLMotionProps } from 'motion/react'
import { m } from 'motion/react'

export const inputVariants = tv({
  base: [
    // base
    'relative flex w-full items-center transition-colors',
    // text color
    'text-tremor-content-emphasis',
    // background color
    'bg-tremor-background dark:bg-tremor-background-muted has-data-[autofilled=true]:bg-tremor-brand-faint',
    // invalid
    'has-aria-invalid:ring-1 has-aria-invalid:ring-red-500 dark:has-aria-invalid:ring-red-400',
    // disabled state
    'has-disabled:opacity-50',
  ],

  compoundVariants: [
    {
      className: 'pr-1.25',
      hasEndAdornment: true,
      size: 'sm',
    },
  ],

  defaultVariants: {
    focusVariant: 'ring',
    roundingVariant: 'default',
    size: 'md',
  },

  slots: {
    icon: 'shrink-0',
    input: [
      'relative block w-full appearance-none border-0 bg-transparent px-0 transition outline-hidden focus:ring-0',
      'placeholder-tremor-content-subtle',
      'disabled:text-tremor-content-subtle',
      '[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden',
    ],
  },

  variants: {
    enableStepper: {
      false: {
        input:
          '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
      },
    },
    focusVariant: {
      'focus-ring': {
        base: 'focus-within:ring-2 focus-within:ring-tremor-brand-subtle',
      },
      none: null,
      ring: {
        base: [
          'ring-1 ring-inset',
          'ring-tremor-border',
          'focus-within:ring-2 focus-within:ring-tremor-brand-subtle',
        ],
      },
    },
    fontVariant: {
      mono: {
        input: 'font-mono placeholder:font-sans',
      },
      normal: null,
      tabular: {
        input: 'tabular-nums',
      },
    },
    hasEndAdornment: {
      true: null,
    },
    roundingVariant: {
      bottom: 'rounded-b-md',
      default: 'rounded-md',
      none: null,
      top: 'rounded-t-md',
    },
    size: {
      flush: {
        base: 'bg-transparent p-0 gap-x-0 dark:bg-transparent',
        icon: 'size-4',
        input: 'py-0 text-sm',
      },
      lg: {
        base: 'px-3 gap-x-2',
        icon: 'size-5',
        input: 'py-3 text-base',
      },
      md: {
        base: 'px-2.5 gap-x-2',
        icon: 'size-5',
        input: 'py-2.25 text-sm',
      },
      sm: {
        base: 'px-2 gap-x-1.5',
        icon: 'size-4',
        input: 'py-2 text-xs',
      },
    },
  },
})

export type AutoSelectAnimationBehavior =
  | 'animate-sweep-rtl'
  | 'animate-sweep-ltr'
  | 'animate-scale'

export interface InputProps
  extends
    Omit<React.ComponentProps<'input'>, 'size'>,
    Omit<VariantProps<typeof inputVariants>, 'hasEndAdornment'> {
  /**
   * @default false
   */
  asChild?: boolean

  /**
   * whether the input should auto-select its value on mount
   *
   * @default 'none'
   */
  autoSelectBehavior?: 'none' | 'select' | AutoSelectAnimationBehavior

  endAdornment?: ReactElement<unknown>
  inputClassName?: string
  /**
   * @default false
   */
  shouldEnablePasswordManagers?: boolean

  startIcon?: ReactElement<unknown>
}

function selectOnMount(input: HTMLInputElement | null) {
  if (input !== null) {
    input.select()
  }
}

export function Input({
  asChild = false,
  autoSelectBehavior = 'none',
  children,
  className,
  enableStepper,
  endAdornment,
  focusVariant,
  fontVariant,
  inputClassName,
  ref,
  roundingVariant,
  shouldEnablePasswordManagers = false,
  size,
  startIcon,
  type,
  ...props
}: InputProps) {
  const resolvedEnableStepper = enableStepper ?? type === 'number'
  const resolvedStartIcon =
    startIcon ??
    (type === 'search' ? (
      <SearchIcon
        className="text-tremor-content-subtle stroke-2"
        aria-hidden="true"
      />
    ) : undefined)
  const classNames = inputVariants({
    enableStepper: resolvedEnableStepper,
    focusVariant,
    fontVariant,
    hasEndAdornment: endAdornment !== undefined,
    roundingVariant,
    size,
  })

  const Component = asChild === true ? Slot : 'input'

  const [animatedSelectionWidth, setAnimatedSelectionWidth] = useState<
    null | number
  >(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const finalRef = useMergeRefs([
    ref,
    inputRef,
    autoSelectBehavior === 'select' ? selectOnMount : undefined,
  ])
  const measureInputValueWidth: RefCallback<HTMLSpanElement> = (element) => {
    if (element) {
      setAnimatedSelectionWidth(element.offsetWidth)
    }
  }
  const shouldAnimateAutoSelect =
    autoSelectBehavior === 'animate-sweep-rtl' ||
    autoSelectBehavior === 'animate-sweep-ltr' ||
    autoSelectBehavior === 'animate-scale'

  return (
    <div
      className={cn(
        classNames.base(),
        animatedSelectionWidth !== null && 'overflow-hidden',
        className,
      )}
    >
      {shouldAnimateAutoSelect && typeof props.value === 'string' ? (
        <span
          key={props.id ?? props.name}
          className={cn(
            classNames.input(),
            inputClassName,
            'pointer-events-none invisible absolute w-auto whitespace-pre',
          )}
          ref={measureInputValueWidth}
        >
          {props.value}
        </span>
      ) : null}

      {shouldAnimateAutoSelect &&
      animatedSelectionWidth !== null &&
      props.value ? (
        <AnimatedSelectionHighlight
          behavior={autoSelectBehavior}
          width={animatedSelectionWidth}
          onAnimationComplete={() => {
            setAnimatedSelectionWidth(null)
            requestAnimationFrame(() => {
              inputRef.current?.select()
            })
          }}
        />
      ) : null}

      {resolvedStartIcon ? (
        <Slot className={cn(classNames.icon())}>{resolvedStartIcon}</Slot>
      ) : null}

      <Component
        ref={finalRef}
        type={type}
        className={cn(
          classNames.input(),
          animatedSelectionWidth !== null && 'caret-transparent',
          inputClassName,
        )}
        {...(shouldEnablePasswordManagers
          ? {}
          : {
              'data-1p-ignore': true,
              'data-bwignore': true,
              'data-lpignore': true,
              'data-protonpass-ignore': true,
            })}
        {...props}
      >
        {asChild === true ? children : null}
      </Component>

      {endAdornment}
    </div>
  )
}

interface AnimatedSelectionHighlightProps extends Omit<
  HTMLMotionProps<'div'>,
  'children'
> {
  behavior: AutoSelectAnimationBehavior
  width: number
}

function AnimatedSelectionHighlight({
  behavior,
  className,
  width,
  ...restProps
}: AnimatedSelectionHighlightProps) {
  let motionProps: Partial<HTMLMotionProps<'div'>>

  if (behavior === 'animate-scale') {
    motionProps = {
      animate: { scale: 1 },
      initial: { scale: 0.8 },
      style: {
        left: 0,
        width,
      },
      transition: { damping: 20, stiffness: 260, type: 'spring' },
    }
  } else {
    motionProps = {
      animate: { width },
      initial: { width: 0 },
      style:
        behavior === 'animate-sweep-ltr'
          ? {
              left: 0,
            }
          : {
              right: `calc(100% - ${width}px)`,
            },
      transition: { duration: 0.25, ease: 'easeOut' },
    }
  }

  return (
    <m.div
      aria-hidden="true"
      {...motionProps}
      className={cn(
        'pointer-events-none absolute top-0 h-full bg-[Highlight]',
        className,
      )}
      {...restProps}
    />
  )
}
